import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import LandingPage from '@/pages/LandingPage';
import Blog from '@/pages/Blog';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import Dashboard from '@/pages/Dashboard';
import Properties from '@/pages/Properties';
import Units from '@/pages/Units';
import Reservations from '@/pages/Reservations';
import Guests from '@/pages/Guests';
import ICalManagement from '@/pages/ICalManagement';
import CleaningJobs from '@/pages/CleaningJobs';
import MaintenanceTickets from '@/pages/MaintenanceTickets';
import Messages from '@/pages/Messages';
import Financials from '@/pages/Financials';
import Invoices from '@/pages/Invoices';
import UsersPage from '@/pages/Users';
import Settings from '@/pages/Settings';
import PropertyDetail from '@/pages/PropertyDetail';
import Reports from '@/pages/Reports';
import StaffRegister from '@/pages/StaffRegister';
import ServicesFees from '@/pages/ServicesFees';
import UnitDetail from '@/pages/UnitDetail';
import SubscriptionsDashboard from '@/pages/SubscriptionsDashboard';
import PaymentSuccess from '@/pages/PaymentSuccess';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import SubscriptionRequired from '@/pages/SubscriptionRequired';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
    </div>
  );
  if (!user) return <LandingPage />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/subscription-required" element={<SubscriptionRequired />} />
      <Route path="/staff-register" element={<PublicRoute><StaffRegister /></PublicRoute>} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/properties" element={<Properties />} />
        <Route path="/properties/:id" element={<PropertyDetail />} />
        <Route path="/units" element={<Units />} />
        <Route path="/units/:id" element={<UnitDetail />} />
        <Route path="/reservations" element={<Reservations />} />
        <Route path="/guests" element={<Guests />} />
        <Route path="/ical" element={<ICalManagement />} />
        <Route path="/cleaning" element={<CleaningJobs />} />
        <Route path="/maintenance" element={<MaintenanceTickets />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/financials" element={<Financials />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/services-fees" element={<ServicesFees />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/subscriptions" element={<SubscriptionsDashboard />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
